import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getErrorMessage } from 'utils';

import { getDsp } from '../organization/organizationSlice';
import { userApi } from './userApi';
import {
  DeleteUserPayload,
  InviteSagaPayload,
  MembersPagingRequestValue,
  MembersPagingResponse,
  UpdateMemberRequestValues,
} from './userApiTypes';
import {
  inviteUserSuccess,
  inviteUserFailure,
  inviteUserStart,
  deleteUserSuccess,
  deleteUserFailure,
  deleteUserStart,
  getMembersStart,
  getMembersSuccess,
  getMembersFailure,
  updateMemberSuccess,
  updateMemberFailure,
  updateMemberStart,
} from './userSlice';

function* inviteUserFlow(action: PayloadAction<InviteSagaPayload>) {
  try {
    const dsp: string = yield select(getDsp);
    const { emails, role, agencies, currentAgencyId } = action.payload;

    yield call(userApi.inviteUser, { emails, role, agencies });

    const getMembersPayload = { page: 0, size: 10000, agencyId: currentAgencyId, email: '' };
    const response: MembersPagingResponse = yield call(userApi.getMembers, getMembersPayload, dsp);

    yield put(inviteUserSuccess(response.data));
  } catch (error) {
    yield put(inviteUserFailure(getErrorMessage(error)));
  }
}

function* getMembersFlow(action: PayloadAction<MembersPagingRequestValue>) {
  try {
    const dsp: string = yield select(getDsp);
    const payload = action.payload;
    const response: MembersPagingResponse = yield call(userApi.getMembers, payload, dsp);

    yield put(getMembersSuccess(response.data));
  } catch (error) {
    yield put(getMembersFailure(getErrorMessage(error)));
  }
}

function* deleteUserFlow(action: PayloadAction<DeleteUserPayload>) {
  try {
    const { subId, agencyId } = action.payload;
    const payload = { page: 0, size: 10000, agencyId: agencyId, email: '' };

    yield call(userApi.deleteUser, subId);
    const dsp: string = yield select(getDsp);
    const response: MembersPagingResponse = yield call(userApi.getMembers, payload, dsp);

    yield put(deleteUserSuccess(response.data));
  } catch (error) {
    yield put(deleteUserFailure(getErrorMessage(error)));
  }
}

function* updateMemberFlow(action: PayloadAction<UpdateMemberRequestValues>) {
  try {
    const dsp: string = yield select(getDsp);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { currentSelectedAgencyId: _, ...rest } = action.payload;

    yield call(userApi.updateMember, rest, dsp);
    const payload = {
      page: 0,
      size: 10000,
      agencyId: action.payload.currentSelectedAgencyId,
      email: '',
    };
    const response: MembersPagingResponse = yield call(userApi.getMembers, payload, dsp);

    yield put(updateMemberSuccess(response.data));
  } catch (error) {
    yield put(updateMemberFailure(getErrorMessage(error)));
  }
}

export function* userSagas() {
  yield all([
    takeLatest(inviteUserStart.type, inviteUserFlow),
    takeLatest(deleteUserStart.type, deleteUserFlow),
    takeLatest(updateMemberStart.type, updateMemberFlow),
    takeLatest(getMembersStart.type, getMembersFlow),
  ]);
}
